// Dependencies
import { FC } from "react";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { useMutation } from "react-query";

// Components
import { Form, Formik } from "formik";
import { Grid, Typography } from "@mui/material";
import Button from "~/components/Common/Button";
import AuthTitle from "~/components/Auth/AuthTitle";
import FormInput from "~/components/Auth/Registration/FormInput";
import FormErrorMessage from "~/components/Common/FormErrorMessage";
import PasswordFormLayout from "~/components/Auth/PasswordFormLayout";

// api
import { forgotPassword } from "~/services/auth";

// Types
import { TQueryError } from "~/types";

// Styles
import { StyledInputWrapper } from "./ForgotPasswordForm.styled";

interface ForgotPasswordFormProps {}

const ForgotPasswordForm: FC<ForgotPasswordFormProps> = () => {
  const { enqueueSnackbar } = useSnackbar();

  const forgotPasswordMutation = useMutation({
    mutationFn: forgotPassword,
    onSuccess: () => {
      enqueueSnackbar(
        "Confirm email success! Check your email and change password!",
        { variant: "success" }
      );
    },
    onError: (error: TQueryError) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const handleSubmitForm = (values: { email: string }) => {
    forgotPasswordMutation.mutate(values.email);
  };

  return (
    <Formik
      initialValues={{
        email: "",
      }}
      // validate form values
      validationSchema={Yup.object({
        email: Yup.string().email().required("Email is required"),
      })}
      // handle submit form
      onSubmit={handleSubmitForm}
    >
      {({ values, errors, touched, handleChange, handleBlur }) => (
        <Form>
          <PasswordFormLayout>
            <Grid item>
              <AuthTitle>Opp! Forgot your password?</AuthTitle>
              <Typography variant={"h4"}>
                Enter your email and We will sent you a change password email!
              </Typography>
            </Grid>

            <StyledInputWrapper>
              <FormInput
                label="Email"
                type="email"
                name="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
              />
              <FormErrorMessage
                hasError={!!(errors.email && touched.email)}
                error={errors.email}
              />
            </StyledInputWrapper>

            <Button
              type={"submit"}
              loading={forgotPasswordMutation.isLoading}
              variant={"contained"}
            >
              Confirm
            </Button>
          </PasswordFormLayout>
        </Form>
      )}
    </Formik>
  );
};

export default ForgotPasswordForm;
